import React, { useState, useRef, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { functions } from '../firebaseConfig';
import './Chatbot.css';

const MessageBubble = ({ msg, onSuggestionClick }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const contentRef = useRef(null);

    // Initial check for overflow to conditionally show the chevron
    useEffect(() => {
        if (contentRef.current && msg.role === 'model') {
            // If content is taller than ~150px, we consider it overflowing
            if (contentRef.current.scrollHeight > 160) {
                setIsOverflowing(true);
            }
        }
    }, [msg.text]);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={`chat-bubble ${msg.role}`}>
            <div
                className={`chat-content-box ${msg.role === 'model' ? (isExpanded ? 'expanded' : 'collapsed') : ''}`}
            >
                <div className="markdown-container" ref={contentRef}>
                    <ReactMarkdown
                        remarkPlugins={[remarkMath]}
                        rehypePlugins={[rehypeKatex]}
                    >
                        {msg.text}
                    </ReactMarkdown>
                </div>

                {msg.role === 'model' && (
                    <>
                        <button
                            className="copy-btn"
                            onClick={() => navigator.clipboard.writeText(msg.text)}
                            title="Copy to clipboard"
                        >
                            <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>content_copy</span>
                        </button>

                        {/* Expand/Collapse Chevron - Only if overflowing or already expanded */}
                        {(isOverflowing || isExpanded) && (
                            <button
                                className={`expand-toggle-btn ${isExpanded ? 'expanded' : ''}`}
                                onClick={toggleExpand}
                                title={isExpanded ? "Collapse" : "Expand"}
                            >
                                <span className="material-symbols-rounded">expand_more</span>
                            </button>
                        )}

                        {/* Learning Actions (Assist Chips) - Only show if fully expanded to avoid clutter in summary? 
                            Actually, viewing suggestions immediately is helpful. Let's keep them visible but below the fold if collapsed.
                            However, the requirements say "Default state: collapsed summary". 
                            If we put them outside the collapsed area, they are always visible. 
                            Let's put them INSIDE the content box so they hide with the unique mask.
                        */}
                        {msg.suggestions && msg.suggestions.length > 0 && (
                            <div className="message-suggestions">
                                {msg.suggestions.map((suggestion, sIdx) => (
                                    <button
                                        key={sIdx}
                                        className="suggestion-chip-action"
                                        onClick={() => onSuggestionClick(suggestion)} // Using passed handler
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const Chatbot = ({ fileName, context }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [suggestedPrompt, setSuggestedPrompt] = useState(null);
    const [hasOpened, setHasOpened] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Generate Suggested Prompt based on context
    useEffect(() => {
        if (context) {
            if (context.type === 'quiz-question') {
                const questionSnippet = context.question.substring(0, 30) + "...";
                if (!context.isCorrect) {
                    setSuggestedPrompt(`For the question "${questionSnippet}", why is the correct answer "${context.correctAnswer}"?`);
                } else {
                    setSuggestedPrompt(`Explain why "${context.optionSelected}" is the correct answer for "${questionSnippet}"`);
                }
            } else if (context.type === 'quiz-tab') {
                setSuggestedPrompt(`Help me study specifically for ${context.tab}.`);
            } else {
                setSuggestedPrompt(null);
            }
        }
    }, [context]);

    // Lock body scroll when chat is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const toggleChat = () => {
        const newState = !isOpen;
        setIsOpen(newState);

        if (newState && !hasOpened) {
            setHasOpened(true);
            // Add initial welcome message if empty
            if (messages.length === 0) {
                setTimeout(() => {
                    const initialMessage = fileName
                        ? `**Hi there! 👋**\n\nI'm ready to help you with **${fileName}**.\n\nHere are a few things you can ask me:\n- Summarize this page\n- Create a quiz from this section\n- Explain the key concepts\n\nWhat would you like to start with?`
                        : `**Hi! I'm Spark, your StudySpark Assistant. 👋**\n\nI help students turn complex PDFs into easy-to-study notes, flashcards, and quizzes.\n\n**You can ask me things like:**\n- "How does StudySpark work?"\n- "Can you write a study plan for me?"\n- "Explain a difficult concept."\n\nOnce you upload a PDF, I can help you with specific questions from your notes!`;

                    setMessages([{
                        role: 'model',
                        text: initialMessage
                    }]);
                }, 400);
            }
        }
    };

    const handleSend = async (e, manualText = null) => {
        if (e) e.preventDefault();

        const textToSend = manualText || input;

        if (!textToSend.trim()) return;

        const userMessage = textToSend.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
        setLoading(true);

        if (suggestedPrompt === userMessage) {
            setSuggestedPrompt(null);
        }

        try {
            const chatFn = httpsCallable(functions, 'chatWithSpark');
            const docId = fileName ? fileName.replace(".pdf", "") : null;

            const response = await chatFn({
                message: userMessage,
                docId: docId,
                context: context,
                history: messages.map(m => ({ role: m.role, message: m.text }))
            });

            const { reply, suggestions } = response.data;

            setMessages(prev => [...prev, {
                role: 'model',
                text: reply,
                suggestions: suggestions
            }]);
        } catch (error) {
            console.error("Chat Error:", error);
            setMessages(prev => [...prev, { role: 'model', text: "Sorry, I encountered an error connecting to the AI. Please try again." }]);
        } finally {
            setLoading(false);
        }
    };

    const applySuggestion = () => {
        if (suggestedPrompt) {
            handleSend(null, suggestedPrompt);
        }
    };

    return (
        <>
            {/* Full Screen Overlay */}
            <div className={`chatbot-overlay ${isOpen ? 'open' : ''}`}>
                <div className="chatbot-content-container">

                    {/* Header */}
                    <div className="chat-header">
                        <div className="chat-header-content">
                            <h4 className="chat-title">StudySpark Assistant</h4>
                            {fileName && <p className="chat-subtitle">{fileName}</p>}
                        </div>
                        <button className="collapse-chat-btn" onClick={toggleChat} title="Collapse Chat">
                            <span className="material-symbols-rounded">keyboard_arrow_down</span>
                        </button>
                    </div>

                    {/* Messages Area */}
                    <div className="chat-messages">
                        {messages.map((msg, idx) => (
                            <MessageBubble
                                key={idx}
                                msg={msg}
                                // We need to pass the suggestion handler helper
                                onSuggestionClick={(text) => handleSend(null, text)}
                            />
                        ))}
                        {loading && (
                            <div className="chat-bubble model typing">
                                <div className="typing-dot"></div>
                                <div className="typing-dot"></div>
                                <div className="typing-dot"></div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Footer Area: Suggestions + Input */}
                    <div className="chat-footer">
                        {/* Suggestions */}
                        {suggestedPrompt && !loading && (
                            <div className="suggestion-area">
                                <button className="suggestion-chip" onClick={applySuggestion}>
                                    <span className="material-symbols-rounded chip-icon">lightbulb_circle</span>
                                    <span className="chip-text">{suggestedPrompt}</span>
                                </button>
                            </div>
                        )}

                        {/* Input */}
                        <form className="chat-input-area" onSubmit={(e) => handleSend(e)}>
                            <input
                                type="text"
                                className="chat-input"
                                placeholder={fileName ? "Ask a question from this PDF..." : "Ask me anything about StudySpark..."}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                autoFocus={isOpen}
                            />
                            <button type="submit" className="send-btn" disabled={!input.trim()}>
                                <span className="material-symbols-rounded">arrow_upward</span>
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* FAB (Only visible when chat is closed) */}
            <button
                className={`chatbot-fab ${isOpen ? 'hidden' : ''}`}
                onClick={toggleChat}
                title="Open Assistant"
            >
                <span className="material-symbols-rounded fab-icon">
                    electric_bolt
                </span>
            </button>
        </>
    );
};

export default Chatbot;
